import { configureStore, isPlain } from '@reduxjs/toolkit';
import { activeApiActionsReducer } from './activeApiActionsModel';
import { advisorReducer } from './advisorModel';
import { apiMiddleware } from './apiMiddleware';
import { clientReducer } from './clientModel';
import { invalidateClientsMiddleWare } from './invalidateClientsMiddleware';
import { clientsReducer } from './clientsModel';
import { clientsSearchTextReducer } from './clientsSearchTextModel';
import { initialState } from './initialState';
import { logTrackerReducer } from './logTrackerModel';
import { invalidateClientMiddleware } from './invalidateClientMiddleware';
import { sessionReducer, invalidateSessionMiddleWare } from './sessionModel';
import { clientEmailSliceReducer } from './clientEmailModel';
import { onboardingSlideTrackerReducer } from './onboardingSlideTrackerModel';
import { emailPreferencesReducer } from './emailPreferencesModel';
import { policyExtractorSliceReducer } from './policyExtractorModel';
import { illustrationSliceReducer } from './illustrationModel';
import { dataTableReducer } from './dataTableModel';
import { resendTemporaryPasswordEmailReducer } from './resendTemporaryPasswordEmailModel';
import { verifyPartnerLinkRequestReducer } from './verifyPartnerLinkRequestModel';
import { advisorHierarchyReducer } from './advisorHierarchyModel';
import { advisorAdminPortalReducer } from './advisorAdminPortalModel';
import { advisorOpportunitiesReducer } from './advisorOpportunitiesModel';
import { sponsorshipReducer } from './sponsorshipModel';
import { storeClientCalculationsMiddleware } from './storeClientCalculationsMiddleware';
import { sponsorshipMiddleware } from './sponsorshipMiddleware';
import LogRocket from 'logrocket';
import { stripeSubscribeSessionReducer } from './stripeSubscribeSessionModel';
import { quotesReducer } from './quotesModel';
import { underwritingModelReducer } from './policyApplicationModel';
import { openAiCompletionSliceReducer } from './openAiCompletionModel';
import { openAiCompletionSearchSliceReducer } from './openAiCompletionSearchModel';
import { concurrentSearchSliceReducer } from './concurrentSearchModel';
import { partnerPolicyApplicationModelReducer } from './partnerPolicyApplicationModel';
import { typeformPayloadReducer } from './typeformPayloadModel';
import { policyApplicationPrefillReducer } from './policyApplicationPrefillModel';
import { aiUnderwritingTypeformReducer } from './aiUnderwritingTypeformModel';
import { advisorBgaCarrierSettingsReducer } from './advisorBgaCarrierSettingsModel';
import { consentReducer } from './consentModel';

const defaultMiddlewareOptions = {
  serializableCheck: {
    isSerializable(value: unknown) {
      return value instanceof Date || value instanceof Error || isPlain(value);
    },
  },
};

const preloadedState = {
  clientsSearchText: localStorage.getItem('clientsSearchText') ?? '',
};

export const store = configureStore({
  reducer: {
    activeApiActions: activeApiActionsReducer,
    advisor: advisorReducer,
    client: clientReducer,
    clients: clientsReducer,
    clientsSearchText: clientsSearchTextReducer,
    logTracker: logTrackerReducer,
    session: sessionReducer,
    clientEmail: clientEmailSliceReducer,
    onboardingSlideTracker: onboardingSlideTrackerReducer,
    emailPreferences: emailPreferencesReducer,
    policyExtractor: policyExtractorSliceReducer,
    illustration: illustrationSliceReducer,
    dataTable: dataTableReducer,
    resendTemporaryPasswordEmail: resendTemporaryPasswordEmailReducer,
    verifyPartnerLinkRequest: verifyPartnerLinkRequestReducer,
    advisorHierarchy: advisorHierarchyReducer,
    advisorAdminPortal: advisorAdminPortalReducer,
    advisorOpportunities: advisorOpportunitiesReducer,
    sponsorship: sponsorshipReducer,
    stripeSubscribeSession: stripeSubscribeSessionReducer,
    quotes: quotesReducer,
    policyApplicationModel: underwritingModelReducer,
    openAiCompletion: openAiCompletionSliceReducer,
    openAiCompletionSearch: openAiCompletionSearchSliceReducer,
    concurrentSearch: concurrentSearchSliceReducer,
    partnerPolicyApplicationModel: partnerPolicyApplicationModelReducer,
    typeformPayload: typeformPayloadReducer,
    policyApplicationPrefill: policyApplicationPrefillReducer,
    aiUnderwritingTypeform: aiUnderwritingTypeformReducer,
    advisorBgaCarrierSettings: advisorBgaCarrierSettingsReducer,
    consent: consentReducer,
  },
  middleware(getDefaultMiddleware) {
    return [
      apiMiddleware,
      invalidateClientsMiddleWare,
      invalidateClientMiddleware,
      storeClientCalculationsMiddleware,
      invalidateSessionMiddleWare,
      sponsorshipMiddleware,
      LogRocket.reduxMiddleware(),
    ].concat(getDefaultMiddleware(defaultMiddlewareOptions));
  },
  preloadedState,
});

export type AppState = typeof initialState;
